import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Section from '../Atoms/Section';
import BigH1 from '../Atoms/BigH1';
import BigH2 from '../Atoms/BigH2';
import BigH3 from '../Atoms/BigH3';
import AnimatedSection from '../Components/AnimatedSection';
import BlueButton from '../Atoms/BlueButton';
import * as api from '../../api';

const Title = styled.div`
  width: 100%;
  border-bottom: 1px solid black;
  padding: 30px 0;
  text-align: left;
  padding-left: 40px;
`;

const StyledSection = styled(Section)`
  /* padding: 40px; */
`;

const InterviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
  padding: 20px;
  margin-top: 30px;
`;

const InterviewGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 40px;
  width: 100%;
  max-width: 90%;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const InterviewWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  text-align: center;
`;

const VideoInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px solid;
  padding: 20px;
  border-top: none;
  width: 100%;
`;

const VideoTitle = styled.h3`
  font-size: 18px;
  color: #333;
  width: 100%;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: left;
  font-weight: bold;
  text-align: left;
  line-height: 1.5;
  min-height: calc(18px * 1.5 * 2);
`;

const VideoDescription = styled.p`
  font-size: 14px;
  color: #555;
  margin-top: 10px;
  width: 100%;
  text-align: left;
`;

const InterviewFrame = styled.iframe`
  width: 100%;
  height: 300px;
  border: none;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 30px;
  gap: 10px;
`;

const PageButton = styled.button`
  padding: 8px 15px;
  background-color: ${props => props.active ? '#007bff' : '#f0f0f0'};
  color: ${props => props.active ? 'white' : 'black'};
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  font-weight: ${props => props.active ? 'bold' : 'normal'};
  transition: all 0.2s;
  
  &:hover {
    background-color: ${props => props.active ? '#007bff' : '#e0e0e0'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FilterContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 20px 0;
  width: 100%;
  max-width: 90%;
`;

const SearchInput = styled.input`
  padding: 10px 15px;
  border: 1px solid #ddd;
  border-radius: 4px;
  width: 100%;
  max-width: 400px;
  font-size: 16px;
`;

const CategoryFilter = styled.div`
  display: flex;
  justify-content: center;
  margin: 10px 0 20px;
  width: 100%;
  max-width: 90%;
  flex-wrap: wrap;
  gap: 10px;
`;

const CategoryButton = styled.button`
  padding: 8px 15px;
  background-color: ${props => props.active ? '#007bff' : '#f0f0f0'};
  color: ${props => props.active ? 'white' : 'black'};
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  font-weight: ${props => props.active ? 'bold' : 'normal'};
  transition: all 0.2s;
  
  &:hover {
    background-color: ${props => props.active ? '#007bff' : '#e0e0e0'};
  }
`;

const decodeHTML = (str) => {
  const textArea = document.createElement('textarea');
  textArea.innerHTML = str;
  return textArea.value;
};

const ITWArchive = () => {
  const [interviews, setInterviews] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const ITEMS_PER_PAGE = 6;

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        setLoading(true);
        const {data : youtubeData} = await api.GetYoutubeData();
        
        if (youtubeData.interviews) {
            setInterviews(youtubeData.interviews);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching videos:', error);
      }
    };

    fetchVideos();
  }, []);

  // Filter interviews based on search term and category
  const filteredInterviews = interviews.filter(interview => {
    const matchesSearch = interview.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
      interview.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = selectedCategory === 'all' || interview.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  // Calculate pagination
  const totalPages = Math.ceil(filteredInterviews.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentInterviews = filteredInterviews.slice(startIndex, endIndex);

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Format publication date
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };

  return (
    <div>
      <AnimatedSection direction="left">
        <Title>
          <BigH2>ARCHIVES INTERVIEWS</BigH2>
        </Title>
      </AnimatedSection>
      
      <StyledSection>
        <AnimatedSection direction="right" delay={300}>
          <BigH3 style={{ marginLeft: '40px', marginTop: '40px' }}>
            Découvrez toutes nos interviews avec des designers et experts en design français
          </BigH3>
        </AnimatedSection>
        
        <FilterContainer>
          <SearchInput
            type="text"
            placeholder="Rechercher une interview..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1); // Reset to first page on search
            }}
          />
        </FilterContainer>
        
        {/* Category filter */}
        {/* <CategoryFilter>
          {categories.map(category => (
            <CategoryButton
              key={category}
              active={selectedCategory === category}
              onClick={() => {
                setSelectedCategory(category);
                setCurrentPage(1); // Reset to first page on category change
              }}
            >
              {category === 'all' ? 'Toutes les catégories' : category}
            </CategoryButton>
          ))}
        </CategoryFilter> */}
        
        {loading ? (
          <p style={{ textAlign: 'center', margin: '40px 0' }}>Chargement des interviews...</p>
        ) : error ? (
          <p style={{ textAlign: 'center', margin: '40px 0', color: 'red' }}>{error}</p>
        ) : filteredInterviews.length === 0 ? (
          <p style={{ textAlign: 'center', margin: '40px 0' }}>Aucune interview trouvée pour cette recherche.</p>
        ) : (
          <>
            <InterviewContainer>
              <InterviewGrid>
                {currentInterviews.map((interview) => (
                  <InterviewWrapper key={interview.id}>
                    <InterviewFrame
                      src={interview.embedUrl}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                    <VideoInfo>
                      <VideoTitle>{interview.title}</VideoTitle>
                      <VideoDescription>
                        <strong>Publié le {formatDate(interview.publishedAt)}</strong>
                        <br />
                        {interview.description.length > 200
                          ? `${interview.description.substring(0, 200)}...`
                          : interview.description}
                      </VideoDescription>
                      <a 
                        href={`https://www.youtube.com/watch?v=${interview.id}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ 
                          marginTop: '10px', 
                          color: '#007bff', 
                          textDecoration: 'none',
                          fontWeight: 'bold'
                        }}
                      >
                        Voir sur YouTube
                      </a>
                    </VideoInfo>
                  </InterviewWrapper>
                ))}
              </InterviewGrid>
            </InterviewContainer>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <PaginationContainer>
                <PageButton 
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  &laquo; Précédent
                </PageButton>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => 
                    page === 1 || 
                    page === totalPages || 
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  )
                  .map((page, index, array) => (
                    <React.Fragment key={page}>
                      {index > 0 && array[index - 1] !== page - 1 && (
                        <span style={{ margin: '0 5px' }}>...</span>
                      )}
                      <PageButton 
                        active={currentPage === page}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </PageButton>
                    </React.Fragment>
                  ))
                }
                
                <PageButton 
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Suivant &raquo;
                </PageButton>
              </PaginationContainer>
            )}
          </>
        )}
        
        <div style={{ textAlign: 'center', margin: '30px 0' }}>
          <a 
            href="https://www.youtube.com/playlist?list=PLNePeRvn_hvV2MpRYHdSXqgjnLzey8twu" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <BlueButton>Voir la playlist YouTube complète</BlueButton>
          </a>
        </div>
      </StyledSection>
    </div>
  );
};

export default ITWArchive;